// Updated QuickRegistration.tsx with all fixes
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Typography,
  Chip,
  Alert,
  Paper,
  IconButton,
  InputAdornment,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Zoom,
  Grow,
  Slide,
  Badge,
  alpha,
  styled,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  CircularProgress,
Slider, Tooltip

} from '@mui/material';
import {
  Print as PrintIcon,
  Payment as PaymentIcon,
  CameraAlt as CameraIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Science as ScienceIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  FlipCameraIos as FlipCameraIosIcon,
  CheckCircle as CheckCircleIcon,
  ZoomIn as ZoomInIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  FamilyRestroom as FamilyIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Diamond as DiamondIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  LocalConvenienceStore as ConvenienceIcon,
  Scanner as ScannerIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoIcon,
  AutoAwesome as AiIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import Webcam from 'react-webcam';
import { useReactToPrint } from 'react-to-print';
import {
  // FIND_PATIENT_BY_MOBILE,
  CHECK_PATIENT_AND_FAMILY,
  // CREATE_PATIENT,
} from '../../graphql/customer.graphql';
import {
  GET_ALL_TESTS,
} from '../../graphql/test.graphql';
import {
  QUICK_REGISTRATION,
} from '../../graphql/registration.graphql';
import {
  PROCESS_PRESCRIPTION,
} from '../../graphql/ai.graphql';
import { Receipt } from '../../components/quick-registration/Receipt';
import { TestCatalog } from '../../components/quick-registration/TestCatalog';
import { PaymentSection } from '../../components/quick-registration/PaymentSection';
import { QRCodes } from '../../components/quick-registration/QRCodes';
import { QuickRegistrationResponse } from '../../types/registration.types';
import { Test, TestsResponse } from '../../types/test.types';
import { Customer } from '../../types/customer.types';
import { ProcessPrescriptionResponse, ParsedPrescriptionData } from '../../types/prescription.types';
import {  MatchedTestDetails } from '../../types/test.types';
import { PrescriptionDisplay } from '../../components/quick-registration/PrescriptionDisplay';
import { PrescriptionPreview } from '../../components/temporary-registration/PrescriptionPreview';
import { PrescriptionTestMatcher } from '../../components/temporary-registration/PrescriptionTestMatcher';

// Premium color palette
const colors = {
  gold: '#C5A572',
  goldLight: '#E5D3B5',
  goldDark: '#8B7355',
  burgundy: '#800020',
  burgundyLight: '#A52A2A',
  navy: '#1A1F2E',
  navyLight: '#2A2F3F',
  cream: '#FDFAF3',
  creamDark: '#F5E6D3',
  charcoal: '#2C2C2C',
  silver: '#C0C0C0',
  emerald: '#046307',
  emeraldLight: '#2E7D32',
  platinum: '#E5E4E2',
  rose: '#B76E79',
  roseLight: '#DAB6B9',
};

// Styled components
const LuxuryCard = styled(Card)({
  background: `linear-gradient(135deg, ${colors.cream} 0%, #FFFFFF 100%)`,
  border: `1px solid ${colors.goldLight}`,
  borderRadius: '16px',
  boxShadow: `0 10px 40px -10px ${alpha(colors.gold, 0.2)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 50%, ${colors.navy} 100%)`,
  },
});

const LuxuryButton = styled(Button)<{ variant?: 'contained' | 'outlined' }>(({ variant }) => ({
  borderRadius: '8px',
  padding: '12px 32px',
  fontWeight: 600,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  transition: 'all 0.3s ease',
  ...(variant === 'contained' && {
    background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.navy} 100%)`,
    color: colors.cream,
    border: 'none',
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
      boxShadow: `0 10px 20px -5px ${alpha(colors.burgundy, 0.4)}`,
      transform: 'translateY(-2px)',
    },
  }),
  ...(variant === 'outlined' && {
    border: `2px solid ${colors.gold}`,
    color: colors.navy,
    background: 'transparent',
    '&:hover': {
      background: alpha(colors.gold, 0.1),
      borderColor: colors.goldDark,
      transform: 'translateY(-2px)',
    },
  }),
}));

const LuxuryTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.gold,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.gold,
  },
});

const LuxuryPaper = styled(Paper)({
  background: `linear-gradient(135deg, ${colors.cream} 0%, #FFFFFF 100%)`,
  borderRadius: '12px',
  border: `1px solid ${alpha(colors.gold, 0.3)}`,
});

const StyledToggleButton = styled(ToggleButton)({
  borderRadius: '8px !important',
  padding: '12px 24px',
  margin: '0 4px',
  '&.Mui-selected': {
    background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
    color: colors.cream,
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.gold} 100%)`,
    },
  },
});

// Camera Dialog with premium styling
const CameraDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCapture: (image: string) => void;
}> = ({ open, onClose, onCapture }) => {
  const webcamRef = useRef<Webcam>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const { enqueueSnackbar } = useSnackbar();

  const handleCapture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      onCapture(imageSrc);
      onClose();
    } else {
      enqueueSnackbar('Failed to capture image. Please try again.', { variant: 'error' });
    }
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
          border: `2px solid ${colors.gold}`,
          boxShadow: `0 20px 60px -15px ${alpha(colors.gold, 0.5)}`,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: colors.gold, fontWeight: 600 }}>
            Capture Prescription
          </Typography>
          <Box>
            <IconButton onClick={toggleCamera} sx={{ color: colors.gold }}>
              <FlipCameraIosIcon />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: colors.rose }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', position: 'relative' }}>
          <Box sx={{ 
            border: `4px solid ${colors.gold}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: `0 0 30px ${alpha(colors.gold, 0.3)}`,
          }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              videoConstraints={{
                facingMode,
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }}
              onUserMediaError={() => {
                enqueueSnackbar('Camera access denied', { variant: 'error' });
              }}
            />
          </Box>
          <Box sx={{ 
            position: 'absolute', 
            top: 20, 
            left: 20, 
            right: 20, 
            bottom: 20, 
            border: `2px dashed ${alpha(colors.gold, 0.5)}`,
            borderRadius: '8px',
            pointerEvents: 'none',
          }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: colors.cream,
            borderColor: colors.gold,
            '&:hover': {
              borderColor: colors.goldLight,
              backgroundColor: alpha(colors.gold, 0.1),
            }
          }}
        >
          Cancel
        </Button>
        <LuxuryButton 
          variant="contained" 
          onClick={handleCapture}
          startIcon={<CameraIcon />}
        >
          Capture & Process
        </LuxuryButton>
      </DialogActions>
    </Dialog>
  );
};

// Family Member Card Component
const FamilyMemberCard: React.FC<{
  member: Customer;
  selected: boolean;
  onSelect: () => void;
}> = ({ member, selected, onSelect }) => {
  const getGenderIcon = () => {
    if (member.Gender?.toLowerCase() === 'male') return <MaleIcon />;
    if (member.Gender?.toLowerCase() === 'female') return <FemaleIcon />;
    return <PersonIcon />;
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Card
        sx={{
          p: 2,
          mb: 1,
          cursor: 'pointer',
          background: selected 
            ? `linear-gradient(135deg, ${alpha(colors.gold, 0.1)} 0%, ${alpha(colors.burgundy, 0.1)} 100%)`
            : colors.cream,
          border: selected 
            ? `2px solid ${colors.gold}`
            : member.IsFamilyHead 
              ? `2px solid ${colors.burgundy}`
              : `1px solid ${alpha(colors.gold, 0.3)}`,
          borderRadius: '12px',
          boxShadow: selected ? `0 5px 20px ${alpha(colors.gold, 0.2)}` : 'none',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 5px 20px ${alpha(colors.gold, 0.3)}`,
            borderColor: colors.gold,
          },
          ...(member.IsFamilyHead && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
            },
          }),
        }}
        onClick={onSelect}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              member.IsFamilyHead && (
                <DiamondIcon sx={{ fontSize: 16, color: colors.gold }} />
              )
            }
          >
            <Avatar sx={{ 
              bgcolor: selected ? colors.gold : (member.IsFamilyHead ? colors.burgundy : colors.silver),
              border: `2px solid ${colors.cream}`,
            }}>
              {getGenderIcon()}
            </Avatar>
          </Badge>
          <Box flex={1}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: member.IsFamilyHead ? 700 : 500,
              color: colors.navy,
            }}>
              {member.CustomerName}
              {member.IsFamilyHead && (
                <Chip 
                  label="Family Head" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: 20,
                    backgroundColor: alpha(colors.gold, 0.2),
                    color: colors.goldDark,
                    border: `1px solid ${colors.gold}`,
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
              Age: {member.Age || 'N/A'} • {member.Relationship || 'N/A'} • {member.Gender || 'N/A'}
            </Typography>
          </Box>
          {selected && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            >
              <CheckIcon sx={{ color: colors.gold }} />
            </motion.div>
          )}
        </Box>
      </Card>
    </motion.div>
  );
};

// Premium Header Component - Single Row Layout
const PremiumHeader: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      mb: 4,
      p: 2,
      background: `linear-gradient(135deg, ${alpha(colors.cream, 0.5)} 0%, ${alpha(colors.platinum, 0.3)} 100%)`,
      borderRadius: '16px',
      border: `1px solid ${alpha(colors.gold, 0.2)}`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 50%, ${colors.navy} 100%)`,
      },
    }}
  >
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
    >
      <Avatar 
        sx={{ 
          width: 56, 
          height: 56, 
          background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
          border: `2px solid ${colors.cream}`,
          boxShadow: `0 5px 15px -5px ${alpha(colors.gold, 0.4)}`,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: alpha(colors.charcoal, 0.7),
            maxWidth: 500,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Chip 
        icon={<AiIcon sx={{ fontSize: 18 }} />}
        label="AI-Powered"
        sx={{
          background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
          color: colors.cream,
          fontWeight: 600,
          px: 1,
          '& .MuiChip-icon': {
            color: colors.cream,
          },
        }}
      />
    </motion.div>
  </Box>
);
// Scan Options Component
const ScanOptions: React.FC<{
  options: {
    useCamera: boolean;
    useUpload: boolean;
    useScanner: boolean;
    autoProcess: boolean;
    extractMobile: boolean;
    scannerSettings?: {
      device?: string;
      resolution?: number;
      colorMode?: 'Color' | 'Gray' | 'Lineart';
      pageSize?: 'A4' | 'Letter' | 'Legal';
      source?: 'ADF' | 'Flatbed';
      duplex?: boolean;
    };
  };
  onChange: (key: string, value: any) => void;
  onScan?: () => void;
  isScanning?: boolean;
}> = ({ options, onChange, onScan, isScanning }) => {
  const [showScannerSettings, setShowScannerSettings] = useState(false);
  const [availableScanners, setAvailableScanners] = useState<any[]>([]);

  useEffect(() => {
    if (options.useScanner) {
      // Fetch available scanners
      fetch('/api/scanners')
        .then(res => res.json())
        .then(data => setAvailableScanners(data))
        .catch(err => console.error('Failed to fetch scanners:', err));
    }
  }, [options.useScanner]);

  const handleScanMode = (_event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode) {
      onChange('useCamera', newMode === 'camera');
      onChange('useUpload', newMode === 'upload');
      onChange('useScanner', newMode === 'scanner');
    }
  };

  const handleScannerSettingChange = (key: string, value: any) => {
    onChange('scannerSettings', {
      ...options.scannerSettings,
      [key]: value
    });
  };

  return (
    <>
      <LuxuryPaper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.navy, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoIcon sx={{ color: colors.gold }} />
          Scan Options
          <Chip
            label="AI Powered"
            size="small"
            sx={{ ml: 1, background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`, color: colors.cream }}
          />
        </Typography>

<Box sx={{ display: 'flex', mb: 2 }}>
  <ToggleButtonGroup
    value={
      options.useCamera ? 'camera' : 
      options.useUpload ? 'upload' : 
      options.useScanner ? 'scanner' : null
    }
    exclusive
    onChange={handleScanMode}
    aria-label="scan mode"
    sx={{
      width: '100%',
      display: 'flex',
      gap: 1,
      '& .MuiToggleButton-root': {
        flex: 1, // Equal width buttons
        borderRadius: '12px !important',
        padding: '12px',
      }
    }}
  >
    <StyledToggleButton value="camera">
      <CameraIcon sx={{ mr: 1 }} />
      Camera
    </StyledToggleButton>

    <StyledToggleButton value="upload">
      <UploadIcon sx={{ mr: 1 }} />
      Upload
    </StyledToggleButton>

    <StyledToggleButton value="scanner">
      <ScannerIcon sx={{ mr: 1 }} />
      Scanner
    </StyledToggleButton>
  </ToggleButtonGroup>
</Box>

        {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            <ToggleButtonGroup
              value={
                options.useCamera ? 'camera' : 
                options.useUpload ? 'upload' : 
                options.useScanner ? 'scanner' : null
              }
              exclusive
              onChange={handleScanMode}
              aria-label="scan mode"
              orientation="vertical" // Makes buttons stack vertically
              sx={{
                width: '100%',
                '& .MuiToggleButton-root': {
                  borderRadius: '12px !important',
                  margin: '4px 0',
                  padding: '12px',
                }
              }}
            >
              <StyledToggleButton value="camera">
                <CameraIcon sx={{ mr: 1 }} />
                Camera
              </StyledToggleButton>
              <StyledToggleButton value="upload">
                <UploadIcon sx={{ mr: 1 }} />
                Upload
              </StyledToggleButton>
              <StyledToggleButton value="scanner">
                <ScannerIcon sx={{ mr: 1 }} />
                Scanner
              </StyledToggleButton>
            </ToggleButtonGroup>
          </Box> */}

        {options.useScanner && (
          <Box sx={{ mb: 2, p: 2, bgcolor: alpha(colors.navy, 0.05), borderRadius: '12px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" sx={{ color: colors.navy }}>
                Scanner Settings
              </Typography>
              <Button
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setShowScannerSettings(true)}
                sx={{ color: colors.gold }}
              >
                Configure
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.7) }}>
              {options.scannerSettings?.device || 'Default Scanner'} • 
              {options.scannerSettings?.resolution || 300}dpi • 
              {options.scannerSettings?.source || 'ADF'}
            </Typography>
            
            {onScan && (
              <Button
                fullWidth
                variant="contained"
                onClick={onScan}
                disabled={isScanning}
                sx={{
                  mt: 2,
                  background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                  color: colors.cream,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.gold} 100%)`,
                  },
                }}
              >
                {isScanning ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: colors.cream }} />
                    Scanning...
                  </Box>
                ) : (
                  'Start Scan'
                )}
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: alpha(colors.gold, 0.3) }} />

        <Box
  sx={{
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1,
    flexWrap: 'wrap'
  }}
>
          <FormControlLabel sx={{ m: 0, flex: 1 }}
            control={
              <Switch
                checked={options.autoProcess}
                onChange={(e) => onChange('autoProcess', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.gold,
                    '&:hover': {
                      backgroundColor: alpha(colors.gold, 0.1),
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.gold,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AutoIcon sx={{ fontSize: 20, color: colors.gold }} />
                <Typography sx={{ color: colors.navy }}>Auto Process</Typography>
              </Box>
            }
          />
          
          <FormControlLabel sx={{ m: 0, flex: 1 }}
            control={
              <Switch
                checked={options.extractMobile}
                onChange={(e) => onChange('extractMobile', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.gold,
                    '&:hover': {
                      backgroundColor: alpha(colors.gold, 0.1),
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.gold,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 20, color: colors.gold }} />
                <Typography sx={{ color: colors.navy }}>Auto Extract Mobile</Typography>
              </Box>
            }
          />
        </Box>
      </LuxuryPaper>

      {/* Scanner Settings Dialog */}
      <Dialog
        open={showScannerSettings}
        onClose={() => setShowScannerSettings(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
            border: `2px solid ${colors.gold}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.gold }}>Scanner Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.cream }}>Scanner Device</InputLabel>
              <Select
                value={options.scannerSettings?.device || ''}
                label="Scanner Device"
                onChange={(e) => handleScannerSettingChange('device', e.target.value)}
                sx={{
                  color: colors.cream,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.gold, 0.3),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.gold,
                  },
                }}
              >
                <MenuItem value="">Default Scanner</MenuItem>
                {availableScanners.map((scanner) => (
                  <MenuItem key={scanner.id} value={scanner.id}>
                    {scanner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography gutterBottom sx={{ color: colors.cream }}>Resolution (DPI)</Typography>
            <Slider
              value={options.scannerSettings?.resolution || 300}
              onChange={(_e, val) => handleScannerSettingChange('resolution', val)}
              step={50}
              marks
              min={150}
              max={600}
              valueLabelDisplay="auto"
              sx={{ mb: 2, color: colors.gold }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.cream }}>Color Mode</InputLabel>
              <Select
                value={options.scannerSettings?.colorMode || 'Color'}
                label="Color Mode"
                onChange={(e) => handleScannerSettingChange('colorMode', e.target.value)}
                sx={{
                  color: colors.cream,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.gold, 0.3),
                  },
                }}
              >
                <MenuItem value="Color">Color</MenuItem>
                <MenuItem value="Gray">Grayscale</MenuItem>
                <MenuItem value="Lineart">Black & White</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.cream }}>Page Size</InputLabel>
              <Select
                value={options.scannerSettings?.pageSize || 'A4'}
                label="Page Size"
                onChange={(e) => handleScannerSettingChange('pageSize', e.target.value)}
                sx={{
                  color: colors.cream,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.gold, 0.3),
                  },
                }}
              >
                <MenuItem value="A4">A4</MenuItem>
                <MenuItem value="Letter">Letter</MenuItem>
                <MenuItem value="Legal">Legal</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.cream }}>Source</InputLabel>
              <Select
                value={options.scannerSettings?.source || 'ADF'}
                label="Source"
                onChange={(e) => handleScannerSettingChange('source', e.target.value)}
                sx={{
                  color: colors.cream,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.gold, 0.3),
                  },
                }}
              >
                <MenuItem value="ADF">Auto Document Feeder</MenuItem>
                <MenuItem value="Flatbed">Flatbed</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={options.scannerSettings?.duplex || false}
                  onChange={(e) => handleScannerSettingChange('duplex', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.gold,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.gold,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: colors.cream }}>Duplex (Double-sided)</Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowScannerSettings(false)} 
            sx={{ 
              color: colors.cream,
              borderColor: colors.gold,
              '&:hover': {
                borderColor: colors.goldLight,
                backgroundColor: alpha(colors.gold, 0.1),
              }
            }}
          >
            Cancel
          </Button>
          <LuxuryButton
            variant="contained"
            onClick={() => setShowScannerSettings(false)}
          >
            Save Settings
          </LuxuryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main Component
export const QuickRegistration: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [mobileNumber, setMobileNumber] = useState('');
  const [patient, setPatient] = useState<Customer | null>(null);
  const [newPatient, setNewPatient] = useState({
    name: '',
    age: '',
    gender: 'Male',
    email: '',
  });
  const [selectedTests, setSelectedTests] = useState<Test[]>([]);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [registration, setRegistration] = useState<any>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [processingAI, setProcessingAI] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState<ParsedPrescriptionData | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [doctorName, setDoctorName] = useState('');
  const [notes, setNotes] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [membershipId, setMembershipId] = useState('');
  const [membershipVerified, setMembershipVerified] = useState(false);
  const [showMobileInput, setShowMobileInput] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Scan options state
  const [scanOptions, setScanOptions] = useState({
    useCamera: true,
    useUpload: false,
    useScanner: false,
    autoProcess: true,
    extractMobile: true,
    scannerSettings: {
      resolution: 300,
      colorMode: 'Color' as 'Color' | 'Gray' | 'Lineart',
      pageSize: 'A4' as 'A4' | 'Letter' | 'Legal',
      source: 'ADF' as 'ADF' | 'Flatbed',
      duplex: false,
    },
  });
  
  const [isScanning, setIsScanning] = useState(false);
  
  // Test selection state for matcher
  const [selectedTestIds, setSelectedTestIds] = useState<number[]>([]);
  
  // Family member related state
  const [familyMembers, setFamilyMembers] = useState<Customer[]>([]);
  const [selectedMember, setSelectedMember] = useState<Customer | null>(null);
  const [showFamilySelector, setShowFamilySelector] = useState(false);
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('all');
  
  const receiptRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  
  // GraphQL hooks
  const [checkPatientAndFamily] = useLazyQuery<CheckPatientAndFamilyResponse>(CHECK_PATIENT_AND_FAMILY);
  const { data: testsData } = useQuery<TestsResponse>(GET_ALL_TESTS);
  const [quickRegistration] = useMutation<QuickRegistrationResponse>(QUICK_REGISTRATION);
  const [processPrescription] = useMutation<ProcessPrescriptionResponse>(PROCESS_PRESCRIPTION);

  // Define response type for combined query
  interface CheckPatientAndFamilyResponse {
    findPatient: Customer | null;
    findFamilyMembers: Customer[];
  }

  const handleScanOptionChange = (key: string, value: any) => {
    setScanOptions(prev => ({ ...prev, [key]: value }));
  };

  // Handle scanner
  const handleScan = async () => {
    setIsScanning(true);
    try {
      // Simulate scanner functionality (replace with actual API call)
      enqueueSnackbar('Scanner functionality - please use camera or upload for now', { 
        variant: 'info' 
      });
      
      // Mock success for now
      setTimeout(() => {
        setIsScanning(false);
      }, 2000);
      
    } catch (error) {
      console.error('Scan error:', error);
      enqueueSnackbar('Failed to scan document', { variant: 'error' });
      setIsScanning(false);
    }
  };

  // Handle mobile number verification with family check
  const handleMobileSubmit = async (num?: string) => {
    const mobileToCheck = num || mobileNumber;
    
    if (mobileToCheck.length !== 10) {
      enqueueSnackbar('Please enter a valid 10-digit mobile number', { variant: 'warning' });
      return;
    }

    setCheckingMobile(true);

    try {
      const { data } = await checkPatientAndFamily({ 
        variables: { mobile: mobileToCheck }
      });
      
      const patient_data = data?.findPatient || null;
      const family = data?.findFamilyMembers || [];

      console.log('Patient check result:', { patient: patient_data, familyLength: family.length });

      // Case 1: No patient or family found - New registration
      if (!patient_data && family.length === 0) {
        enqueueSnackbar('New patient. Please complete details.', { variant: 'info' });
        setActiveStep(1);
      }
      
      // Case 2: Patient exists but no family members
      else if (patient_data && family.length === 0) {
        setPatient(patient_data);
        setSelectedMember(patient_data);
        enqueueSnackbar(`Welcome back, ${patient_data.CustomerName}!`, { variant: 'success' });
        setActiveStep(1);
      }
      
      // Case 3: Family members exist (with or without patient)
      else if (family.length > 0) {
        setFamilyMembers(family);
        
        // If patient exists, pre-select them
        if (patient_data) {
          setSelectedMember(patient_data);
          setPatient(patient_data);
        } else {
          // Otherwise select family head
          const familyHead = family.find(m => m.IsFamilyHead);
          if (familyHead) {
            setSelectedMember(familyHead);
            setPatient(familyHead);
          }
        }
        
        setShowFamilySelector(true);
        enqueueSnackbar(
          `Found ${family.length} family member${family.length > 1 ? 's' : ''}. Please select one to continue.`, 
          { variant: 'info' }
        );
      }

    } catch (error) {
      console.error('Error checking patient and family:', error);
      enqueueSnackbar('Error verifying mobile number', { variant: 'error' });
    } finally {
      setCheckingMobile(false);
    }
  };

  // Handle family member selection
  const handleSelectMember = (member: Customer) => {
    setSelectedMember(member);
    setPatient(member);
    setShowFamilySelector(false);
    setActiveStep(1);
    enqueueSnackbar(`Selected: ${member.CustomerName}`, { variant: 'success' });
  };

  // Handle skip family selection
  const handleSkipFamilySelection = () => {
    if (selectedMember) {
      setShowFamilySelector(false);
      setActiveStep(1);
    } else {
      enqueueSnackbar('Please select a family member or add a new one', { variant: 'warning' });
    }
  };

  // Handle continue without family selection
  const handleContinueAsGuest = () => {
    setShowFamilySelector(false);
    setActiveStep(1);
  };

  // Filter family members based on search and relationship
  const filteredFamilyMembers = familyMembers.filter(member => {
    const matchesSearch = searchTerm === '' || 
      member.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.MobileNumber?.includes(searchTerm);
    
    const matchesRelationship = relationshipFilter === 'all' || member.Relationship === relationshipFilter;
    
    return matchesSearch && matchesRelationship;
  });

  // Items per page for family members
  const ITEMS_PER_PAGE = 10;

  // Paginated family members
  const paginatedMembers = filteredFamilyMembers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get unique relationships for filter
  const getUniqueRelationships = () => {
    const relationships = familyMembers
      .map(m => m.Relationship)
      .filter((r): r is string => !!r)
      .filter((r, i, self) => self.indexOf(r) === i);
    return ['all', ...relationships];
  };

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Handle camera capture
  const handleCapture = useCallback(async (imageSrc: string) => {
    if (!imageSrc) {
      enqueueSnackbar('Failed to capture image', { variant: 'error' });
      return;
    }

    if (!imageSrc.startsWith('data:image/')) {
      enqueueSnackbar('Invalid image format', { variant: 'error' });
      return;
    }

    setCapturedImage(imageSrc);
    setShowPreview(true);
    if (scanOptions.autoProcess) {
      await processPrescriptionImage(imageSrc);
    }
  }, [scanOptions.autoProcess]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      enqueueSnackbar('File too large. Max size is 10MB', { variant: 'error' });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Invalid file type. Please upload JPG, PNG or PDF', { variant: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageData = e.target?.result as string;
      if (imageData) {
        setCapturedImage(imageData);
        setShowPreview(true);
        if (scanOptions.autoProcess) {
          await processPrescriptionImage(imageData);
        }
      }
    };
    reader.onerror = () => {
      enqueueSnackbar('Error reading file', { variant: 'error' });
    };
    reader.readAsDataURL(file);
  };

  const handleManualProcess = () => {
    if (capturedImage) {
      processPrescriptionImage(capturedImage);
    }
  };

  const processPrescriptionImage = async (imageData: string) => {
    if (!imageData) {
      enqueueSnackbar('No image data to process', { variant: 'error' });
      return;
    }

    setProcessingAI(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const result = await processPrescription({
        variables: { 
          image: imageData
        },
      });
      
      clearInterval(interval);
      setUploadProgress(100);
      
      if (result.data?.processPrescription) {
        const response = result.data.processPrescription;
        
        if (!response.success) {
          enqueueSnackbar(response.error || 'Failed to process prescription', { 
            variant: 'error' 
          });
          return;
        }
        
        const parsed = response.parsedData;
        
        // Update selected test IDs from matched tests
        if (parsed.tests && parsed.tests.length > 0) {
          const matchedIds = parsed.tests
            .filter(t => t.matchedTest)
            .map(t => t.matchedTest!.TestId);
          setSelectedTestIds(matchedIds);
          
          // Also set selectedTests for backward compatibility
          const matchedTests = testsData?.tests.filter(t => 
            matchedIds.includes(t.TestId)
          ) || [];
          setSelectedTests(matchedTests);
        }
        
        setPrescriptionData(parsed);
        
        // Auto-fill patient details
        if (parsed.patientName || parsed.age || parsed.gender) {
          setNewPatient({
            name: parsed.patientName || newPatient.name,
            age: parsed.age?.toString() || newPatient.age,
            gender: parsed.gender || newPatient.gender,
            email: newPatient.email,
          });
        }
        
        if (parsed.doctorName) {
          setDoctorName(parsed.doctorName);
        }
        
        // Auto-check mobile if enabled
        if (scanOptions.extractMobile && parsed.mobileNumber) {
          setMobileNumber(parsed.mobileNumber);
          setTimeout(() => {
            handleMobileSubmit(parsed.mobileNumber);
          }, 500);
        } else if (!parsed.mobileNumber) {
          setShowMobileInput(true);
          enqueueSnackbar('Mobile number not found. Please enter manually.', { variant: 'info' });
        }
        
        setActiveStep(1);
        enqueueSnackbar('Prescription processed successfully!', { variant: 'success' });
      }
    } catch (error: any) {
      clearInterval(interval);
      console.error('AI processing error:', error);
      enqueueSnackbar('AI processing failed. Please try again.', { variant: 'error' });
    } finally {
      setProcessingAI(false);
    }
  };

  // Test selection handlers for matcher
  const handleTestSelect = (test: MatchedTestDetails) => {
    setSelectedTestIds(prev => {
      if (prev.includes(test.TestId)) {
        return prev.filter(id => id !== test.TestId);
      } else {
        return [...prev, test.TestId];
      }
    });
    
    // Also update selectedTests for backward compatibility
    const fullTest = testsData?.tests.find(t => t.TestId === test.TestId);
    if (fullTest) {
      setSelectedTests(prev => {
        if (prev.find(t => t.TestId === test.TestId)) {
          return prev.filter(t => t.TestId !== test.TestId);
        } else {
          return [...prev, fullTest];
        }
      });
    }
  };

  const handleConfirmTests = () => {
    setActiveStep(2);
    enqueueSnackbar(`${selectedTestIds.length} tests selected`, { variant: 'success' });
  };

  // Auto-fill patient details handler
  const handleAutoFillPatient = () => {
    if (prescriptionData) {
      setNewPatient({
        name: prescriptionData.patientName || newPatient.name,
        age: prescriptionData.age?.toString() || newPatient.age,
        gender: prescriptionData.gender || newPatient.gender,
        email: newPatient.email,
      });
      if (prescriptionData.doctorName) {
        setDoctorName(prescriptionData.doctorName);
      }
      if (prescriptionData.mobileNumber) {
        setMobileNumber(prescriptionData.mobileNumber);
      }
      enqueueSnackbar('Patient details auto-filled!', { variant: 'success' });
    }
  };

  // Auto-select tests handler
  const handleAutoSelectTests = () => {
    if (prescriptionData?.tests && testsData?.tests) {
      const matchedIds = prescriptionData.tests
        .filter(t => t.matchedTest)
        .map(t => t.matchedTest!.TestId);
      setSelectedTestIds(matchedIds);
      
      const matchedTests = testsData.tests.filter(t => 
        matchedIds.includes(t.TestId)
      );
      setSelectedTests(matchedTests);
      enqueueSnackbar(`Selected ${matchedTests.length} tests from prescription!`, { variant: 'success' });
    }
  };

  // Handle test toggle from PrescriptionDisplay (legacy)
  const handleTestToggle = (testName: string) => {
    const test = testsData?.tests.find(t => 
      t.TestName.toLowerCase().includes(testName.toLowerCase()) ||
      testName.toLowerCase().includes(t.TestName.toLowerCase())
    );
    
    if (test) {
      setSelectedTests(prev => {
        const exists = prev.find(t => t.TestId === test.TestId);
        if (exists) {
          return prev.filter(t => t.TestId !== test.TestId);
        }
        return [...prev, test];
      });
      
      setSelectedTestIds(prev => {
        if (prev.includes(test.TestId)) {
          return prev.filter(id => id !== test.TestId);
        } else {
          return [...prev, test.TestId];
        }
      });
    }
  };

  const handleTestSelection = (test: Test) => {
    setSelectedTests(prev => {
      const exists = prev.find(t => t.TestId === test.TestId);
      if (exists) {
        return prev.filter(t => t.TestId !== test.TestId);
      }
      return [...prev, test];
    });
    
    setSelectedTestIds(prev => {
      if (prev.includes(test.TestId)) {
        return prev.filter(id => id !== test.TestId);
      } else {
        return [...prev, test.TestId];
      }
    });
  };

  const calculateTotal = () => {
    return selectedTests.reduce((sum, test) => sum + Number(test.Amount), 0);
  };

  const handlePayment = async () => {
    if (selectedTests.length === 0) {
      enqueueSnackbar('Please select at least one test', { variant: 'warning' });
      return;
    }

    if (!patient && !newPatient.name) {
      enqueueSnackbar('Please complete patient details', { variant: 'warning' });
      setActiveStep(0);
      return;
    }

    try {
      const { data } = await quickRegistration({
        variables: {
          input: {
            mobile: patient?.MobileNumber || mobileNumber,
            name: patient?.CustomerName || newPatient.name,
            age: patient?.Age || parseInt(newPatient.age),
            gender: patient?.Gender || newPatient.gender,
            email: patient?.EmailId || newPatient.email,
            branchId: 1,
            testIds: selectedTests.map(t => t.TestId),
            discountAmount,
            paymentMethod,
            doctorName,
            notes,
          },
        },
      });
      
      if (data?.quickRegistration) {
        setRegistration(data.quickRegistration);
        enqueueSnackbar('Registration completed!', { variant: 'success' });
        setActiveStep(4);
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt-${registration?.registrationNo}`,
  });

  const handleSendWhatsApp = () => {
    if (registration) {
      window.open(`https://wa.me/${registration.patient.mobile}?text=Your%20registration%20is%20confirmed.%20Reg%20No:%20${registration.registrationNo}`);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setMobileNumber('');
    setPatient(null);
    setNewPatient({ name: '', age: '', gender: 'Male', email: '' });
    setSelectedTests([]);
    setSelectedTestIds([]);
    setRegistration(null);
    setPrescriptionData(null);
    setDoctorName('');
    setNotes('');
    setDiscountAmount(0);
    setMembershipId('');
    setMembershipVerified(false);
    setShowMobileInput(false);
    setCapturedImage(null);
    setShowPreview(false);
    setFamilyMembers([]);
    setSelectedMember(null);
    setShowFamilySelector(false);
  };

  const handleVerifyMembership = () => {
    if (membershipId) {
      setMembershipVerified(true);
      setDiscountAmount(500);
      enqueueSnackbar('Membership verified! Discount applied.', { variant: 'success' });
    }
  };

  const steps = [
    { label: 'Upload Prescription', icon: <CameraIcon /> },
    { label: 'Review & Confirm', icon: <CheckIcon /> },
    { label: 'Select Tests', icon: <ScienceIcon /> },
    { label: 'Payment', icon: <PaymentIcon /> },
    { label: 'QR & Receipt', icon: <PrintIcon /> },
  ];

  // Fix for missing CircularProgress import
  // const CircularProgress = LinearProgress;

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5 }}
          >
            <LuxuryCard elevation={3}>
              <CardContent sx={{ p: 2 }}>

                <PremiumHeader
                  title="Upload Prescription"
                  subtitle="Experience the future of diagnostics with our AI-powered prescription scanning"
                  icon={<CameraIcon sx={{ fontSize: 32 }} />}
                />

                

                {/* Two Column Layout for Scan Options and Preview */}
                <Grid container spacing={3}>
                  {/* Left Column - Scan Options and Buttons (3 columns) */}
                  <Grid size={{xs:12,sm:4}}>
                    {/* Scan Options */}
                    <ScanOptions 
                      options={scanOptions} 
                      onChange={handleScanOptionChange} 
                      onScan={handleScan}
                      isScanning={isScanning}
                    />

                    {/* Camera Option Button */}
                    {scanOptions.useCamera && (
                      <Box sx={{ mb: 2 }}>
                        <Paper
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                            borderRadius: '16px',
                            boxShadow: `0 10px 20px -5px ${alpha(colors.gold, 0.3)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 15px 30px -5px ${alpha(colors.gold, 0.5)}`,
                            },
                          }}
                          onClick={() => setShowCamera(true)}
                        >
                          <CameraIcon sx={{ fontSize: 48, color: colors.cream, mb: 1 }} />
                          <Typography variant="h6" sx={{ color: colors.cream, fontWeight: 600 }}>
                            Take Photo
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha(colors.cream, 0.8) }}>
                            Use camera to capture prescription
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Upload Option Button */}
                    {scanOptions.useUpload && (
                      <Box sx={{ mb: 2 }}>
                        <Paper
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.navy} 100%)`,
                            borderRadius: '16px',
                            boxShadow: `0 10px 20px -5px ${alpha(colors.burgundy, 0.3)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 15px 30px -5px ${alpha(colors.burgundy, 0.5)}`,
                            },
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <UploadIcon sx={{ fontSize: 48, color: colors.cream, mb: 1 }} />
                          <Typography variant="h6" sx={{ color: colors.cream, fontWeight: 600 }}>
                            Upload File
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha(colors.cream, 0.8) }}>
                            JPG, PNG or PDF (max 10MB)
                          </Typography>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*,.pdf"
                            style={{ display: 'none' }}
                          />
                        </Paper>
                      </Box>
                    )}

                    {/* Scanner Option Button */}
                    {scanOptions.useScanner && (
                      <Box sx={{ mb: 2 }}>
                        <Paper
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.gold} 100%)`,
                            borderRadius: '16px',
                            boxShadow: `0 10px 20px -5px ${alpha(colors.navy, 0.3)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 15px 30px -5px ${alpha(colors.navy, 0.5)}`,
                            },
                          }}
                          onClick={handleScan}
                        >
                          <ScannerIcon sx={{ fontSize: 48, color: colors.cream, mb: 1 }} />
                          <Typography variant="h6" sx={{ color: colors.cream, fontWeight: 600 }}>
                            Scan Document
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha(colors.cream, 0.8) }}>
                            Use scanner to capture prescription
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    
                  </Grid>

                  {/* Right Column - Prescription Preview (9 columns) */}
                  <Grid size={{xs:12,sm:8}}>
                    {showPreview && capturedImage ? (
                      <PrescriptionPreview
                        imageUrl={capturedImage}
                        onClose={() => setShowPreview(false)}
                        onDownload={() => {
                          const link = document.createElement('a');
                          link.href = capturedImage;
                          link.download = 'prescription.jpg';
                          link.click();
                        }}
                        extractedData={prescriptionData || undefined}
                      />
                    ) : (
                      <Paper
                        sx={{
                          height: '100%',
                          minHeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(colors.navy, 0.9)} 0%, ${alpha(colors.burgundy, 0.9)} 100%)`,
                          borderRadius: '16px',
                          border: `2px dashed ${alpha(colors.gold, 0.3)}`,
                          boxShadow: `0 20px 40px -10px ${alpha(colors.gold, 0.2)}`,
                        }}
                      >
                        <Box textAlign="center" p={4}>
                          <CameraIcon sx={{ fontSize: 80, color: alpha(colors.gold, 0.5), mb: 2 }} />
                          <Typography variant="h4" sx={{ color: alpha(colors.cream, 0.7), fontWeight: 300, mb: 1 }}>
                            No Prescription Selected
                          </Typography>
                          <Typography variant="body1" sx={{ color: alpha(colors.cream, 0.5) }}>
                            Choose an option on the left to preview your prescription
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </Grid>
                </Grid>

{/* Processing Status */}
                    {processingAI && (
                      <Grow in={true}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" gutterBottom align="center" sx={{ color: colors.navy }}>
                            Processing with AI...
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress} 
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: alpha(colors.gold, 0.2),
                              '& .MuiLinearProgress-bar': {
                                background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                                borderRadius: 4,
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.7) }} align="center" display="block">
                            {uploadProgress}% - Extracting patient details and tests
                          </Typography>
                        </Box>
                      </Grow>
                    )}

                    {/* Manual Process Button */}
                    {showPreview && !scanOptions.autoProcess && !processingAI && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <LuxuryButton onClick={handleManualProcess} fullWidth>
                          Process Prescription
                        </LuxuryButton>
                      </Box>
                    )}
                {/* Mobile Number Input for Patient Check */}
                <Grow in={true} timeout={800}>
                  <LuxuryPaper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: colors.navy,
                      fontWeight: 600,
                    }}>
                      <PhoneIcon sx={{ color: colors.gold }} />
                      Check Existing Patient
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{xs:12,sm:8}}>
                        <LuxuryTextField
                          fullWidth
                          label="Mobile Number"
                          value={mobileNumber}
                          onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="Enter 10-digit mobile number"
                          disabled={checkingMobile}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ color: colors.gold }} />
                                +91
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid size={{xs:12,sm:4}}>
                        <LuxuryButton
                          fullWidth
                          variant="contained"
                          onClick={() => handleMobileSubmit()}
                          disabled={mobileNumber.length !== 10 || checkingMobile}
                        >
                          {checkingMobile ? 'Checking...' : 'Check'}
                        </LuxuryButton>
                      </Grid>
                    </Grid>
                  </LuxuryPaper>
                </Grow>

                {/* Family Member Selector */}
                {showFamilySelector && (
                  <Zoom in={true} timeout={500}>
                    <LuxuryPaper elevation={3} sx={{ p: 3, mb: 3 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          color: colors.navy,
                          fontWeight: 600,
                        }}>
                          <FamilyIcon sx={{ color: colors.gold }} />
                          Family Members ({familyMembers.length})
                        </Typography>
                      </Box>

                      {/* Search and Filter */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <LuxuryTextField
                          size="small"
                          placeholder="Search by name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: colors.gold }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ flex: 2 }}
                        />
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel sx={{ color: colors.navy }}>Relationship</InputLabel>
                          <Select
                            value={relationshipFilter}
                            label="Relationship"
                            onChange={(e) => setRelationshipFilter(e.target.value as string)}
                            sx={{
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(colors.gold, 0.3),
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.gold,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.gold,
                                borderWidth: '2px',
                              },
                            }}
                          >
                            {getUniqueRelationships().map(rel => (
                              <MenuItem key={rel} value={rel}>
                                {rel === 'all' ? 'All Relationships' : rel}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Family Members List */}
                      <Box sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
                        {paginatedMembers.length === 0 ? (
                          <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                            No family members match your search.
                          </Typography>
                        ) : (
                          <>
                            {paginatedMembers.map((member) => (
                              <FamilyMemberCard
                                key={member.CustomerId}
                                member={member}
                                selected={selectedMember?.CustomerId === member.CustomerId}
                                onSelect={() => handleSelectMember(member)}
                              />
                            ))}

                            {/* Pagination */}
                            {filteredFamilyMembers.length > ITEMS_PER_PAGE && (
                              <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
                                <Pagination
                                  count={Math.ceil(filteredFamilyMembers.length / ITEMS_PER_PAGE)}
                                  page={currentPage}
                                  onChange={handlePageChange}
                                  color="primary"
                                  size="small"
                                  sx={{
                                    '& .MuiPaginationItem-root': {
                                      color: colors.navy,
                                      '&.Mui-selected': {
                                        backgroundColor: colors.gold,
                                        color: colors.cream,
                                      },
                                    },
                                  }}
                                />
                              </Stack>
                            )}
                          </>
                        )}
                      </Box>

                      <Divider sx={{ my: 2, borderColor: alpha(colors.gold, 0.3) }} />

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <LuxuryButton 
                          variant="outlined" 
                          onClick={handleContinueAsGuest}
                        >
                          Continue as Guest
                        </LuxuryButton>
                        <LuxuryButton 
                          variant="contained" 
                          onClick={handleSkipFamilySelection}
                          disabled={!selectedMember}
                        >
                          Continue with Selected
                        </LuxuryButton>
                      </Box>
                    </LuxuryPaper>
                  </Zoom>
                )}

              </CardContent>
              
            </LuxuryCard>
          </motion.div>
        );

      case 1:
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.5 }}
    >
      <LuxuryCard elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <PremiumHeader
            title="Review & Confirm"
            subtitle="Verify the extracted information and complete patient details"
            icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
          />

          {/* Mobile Number Input (if not found) - Full Width at Top */}
          {showMobileInput && (
            <Slide direction="up" in={true} timeout={500}>
              <LuxuryPaper elevation={2} sx={{ 
                p: 3, 
                mb: 4, 
                background: `linear-gradient(135deg, ${alpha(colors.gold, 0.1)} 0%, ${alpha(colors.burgundy, 0.1)} 100%)`,
                border: `2px solid ${colors.gold}`,
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: colors.navy,
                  fontWeight: 600,
                }}>
                  <PhoneIcon sx={{ color: colors.gold }} />
                  Mobile Number Required
                </Typography>
                <Typography variant="body2" paragraph sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Mobile number was not found in the prescription. Please enter it manually to continue.
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs:12 , sm:8 }}>
                    <LuxuryTextField
                      fullWidth
                      label="Mobile Number"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit mobile number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: colors.gold }} />
                            +91
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs:12 , sm:4 }}>
                    <LuxuryButton
                      fullWidth
                      variant="contained"
                      onClick={() => handleMobileSubmit()}
                      disabled={mobileNumber.length !== 10}
                    >
                      Verify
                    </LuxuryButton>
                  </Grid>
                </Grid>
              </LuxuryPaper>
            </Slide>
          )}

          {/* Split View: Prescription Display on Left, Image on Right */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Left Side - Prescription Display Component */}
            <Grid size={{ xs:12 , sm:7 }}>
              {prescriptionData && (
                <PrescriptionDisplay
                  data={prescriptionData}
                  onAutoFillPatient={handleAutoFillPatient}
                  onAutoSelectTests={handleAutoSelectTests}
                  onTestToggle={handleTestToggle}
                  selectedTests={selectedTests.map(t => t.TestName)}
                />
              )}
            </Grid>

            {/* Right Side - Prescription Image */}
{capturedImage && (
  <Grid size={{ xs:12 , sm:5 }}>
    <Zoom in={true} timeout={800}>
      <LuxuryPaper 
        elevation={3} 
        sx={{ 
          p: 2, 
          position: 'sticky', 
          top: 20,
          height: '600px', // Fixed height instead of maxHeight
          maxHeight: '600px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header - Fixed at top */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: colors.navy, fontWeight: 600 }}>
            Prescription Image
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Zoom Out">
              <IconButton 
                size="small" 
                onClick={() => {
                  if (imageRef.current) {
                    const currentScale = parseFloat(imageRef.current.dataset.scale || '1');
                    const newScale = Math.max(0.5, currentScale - 0.25);
                    imageRef.current.style.transform = `scale(${newScale})`;
                    imageRef.current.dataset.scale = newScale.toString();
                  }
                }}
                sx={{ color: colors.gold }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Zoom In">
              <IconButton 
                size="small" 
                onClick={() => {
                  if (imageRef.current) {
                    const currentScale = parseFloat(imageRef.current.dataset.scale || '1');
                    const newScale = Math.min(3, currentScale + 0.25);
                    imageRef.current.style.transform = `scale(${newScale})`;
                    imageRef.current.dataset.scale = newScale.toString();
                  }
                }}
                sx={{ color: colors.gold }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Fit to Page">
              <IconButton 
                size="small" 
                onClick={() => {
                  if (imageRef.current && imageContainerRef.current) {
                    imageRef.current.style.transform = 'scale(1)';
                    imageRef.current.dataset.scale = '1';
                    
                    // Scroll to top
                    imageContainerRef.current.scrollTop = 0;
                    imageContainerRef.current.scrollLeft = 0;
                  }
                }}
                sx={{ color: colors.gold }}
              >
                <FitScreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Zoom Level Indicator - Fixed below header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.6) }}>
            Zoom:
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Slider
              size="small"
              value={imageRef.current ? parseFloat(imageRef.current.dataset.scale || '1') * 100 : 100}
              min={50}
              max={300}
              step={25}
              onChange={(_e: Event, value: number | number[]) => {
                if (imageRef.current) {
                  const newScale = (value as number) / 100;
                  imageRef.current.style.transform = `scale(${newScale})`;
                  imageRef.current.dataset.scale = newScale.toString();
                }
              }}
              sx={{
                color: colors.gold,
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                },
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: colors.gold, fontWeight: 600, minWidth: 45 }}>
            {imageRef.current ? Math.round(parseFloat(imageRef.current.dataset.scale || '1') * 100) : 100}%
          </Typography>
        </Box>

        {/* Image Container - Takes remaining space with flex */}
        <Box 
          ref={imageContainerRef}
          sx={{ 
            flex: 1, // Take all remaining space
            overflow: 'auto', 
            textAlign: 'center',
            border: `2px solid ${alpha(colors.gold, 0.3)}`,
            borderRadius: '8px',
            minHeight: 0, // Important for flex child
            position: 'relative',
            cursor: imageRef.current && parseFloat(imageRef.current.dataset.scale || '1') > 1 ? 'grab' : 'zoom-in',
            '&:active': {
              cursor: imageRef.current && parseFloat(imageRef.current.dataset.scale || '1') > 1 ? 'grabbing' : 'zoom-in',
            },
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: alpha(colors.gold, 0.1),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: colors.gold,
              borderRadius: '4px',
              '&:hover': {
                background: colors.burgundy,
              },
            },
          }}
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
            const container = e.currentTarget;
            if (imageRef.current && parseFloat(imageRef.current.dataset.scale || '1') > 1) {
              const startX = e.pageX - container.scrollLeft;
              const startY = e.pageY - container.scrollTop;
              
              const onMouseMove = (moveEvent: MouseEvent) => {
                moveEvent.preventDefault();
                container.scrollLeft = startX - moveEvent.pageX;
                container.scrollTop = startY - moveEvent.pageY;
              };
              
              const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
              };
              
              document.addEventListener('mousemove', onMouseMove);
              document.addEventListener('mouseup', onMouseUp);
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '100%',
              minHeight: '100%',
            }}
          >
            <motion.img
              ref={imageRef}
              src={capturedImage}
              alt="Prescription"
              data-scale="1"
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                transform: 'scale(1)',
                transformOrigin: 'center',
                transition: 'transform 0.2s ease',
              }}
              drag={imageRef.current ? parseFloat(imageRef.current.dataset.scale || '1') > 1 : false}
              dragConstraints={imageContainerRef}
              dragElastic={0}
              dragMomentum={false}
              onDragStart={(event: any) => {
                if (imageRef.current) {
                  const scale = parseFloat(imageRef.current.dataset.scale || '1');
                  if (scale > 1) {
                    const target = event.target as HTMLElement;
                    target.style.cursor = 'grabbing';
                  }
                }
              }}
              onDragEnd={(event: any) => {
                if (imageRef.current) {
                  const scale = parseFloat(imageRef.current.dataset.scale || '1');
                  const target = event.target as HTMLElement;
                  target.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
                }
              }}
            />
          </Box>
        </Box>

        {/* Image Info Footer - Fixed at bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.6) }}>
            Drag to pan • Scroll to zoom
          </Typography>
          <Chip
            size="small"
            label={imageRef.current ? `${Math.round(parseFloat(imageRef.current.dataset.scale || '1') * 100)}%` : '100%'}
            sx={{
              backgroundColor: alpha(colors.gold, 0.1),
              border: `1px solid ${colors.gold}`,
              color: colors.navy,
              height: 20,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.7rem',
              },
            }}
          />
        </Box>
      </LuxuryPaper>
    </Zoom>
  </Grid>
)}
          </Grid>

          {/* Prescription Test Matcher - Full Width Below */}
          {prescriptionData && prescriptionData.tests && prescriptionData.tests.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <PrescriptionTestMatcher
                extractedTests={prescriptionData.tests}
                unmatchedTests={prescriptionData.unmatchedTests || []}
                onTestSelect={handleTestSelect}
                selectedTests={selectedTestIds}
                onConfirmSelection={handleConfirmTests}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <LuxuryButton variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </LuxuryButton>
            <LuxuryButton 
              variant="contained" 
              onClick={() => setActiveStep(2)}
              disabled={!newPatient.name || !newPatient.age || (!patient && !mobileNumber)}
            >
              Continue to Test Selection
            </LuxuryButton>
          </Box>
        </CardContent>
      </LuxuryCard>
    </motion.div>
  );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
          >
            <LuxuryCard elevation={3}>
              <CardContent sx={{ p: 4 }}>
                <PremiumHeader
                  title="Select Tests"
                  subtitle="Choose from our comprehensive range of diagnostic tests"
                  icon={<ScienceIcon sx={{ fontSize: 32 }} />}
                />

                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3,
                    backgroundColor: alpha(colors.gold, 0.1),
                    color: colors.navy,
                    border: `1px solid ${colors.gold}`,
                    '& .MuiAlert-icon': {
                      color: colors.gold,
                    },
                  }}
                >
                  Tests from your prescription are already selected. You can add or remove tests below.
                </Alert>

                <TestCatalog
                  tests={testsData?.tests || []}
                  selectedTests={selectedTests}
                  onTestSelect={handleTestSelection}
                  loading={false}
                />

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <LuxuryButton variant="outlined" onClick={() => setActiveStep(1)}>
                    Back
                  </LuxuryButton>
                  <LuxuryButton 
                    variant="contained" 
                    onClick={() => setActiveStep(3)}
                    disabled={selectedTests.length === 0}
                  >
                    Continue to Payment
                  </LuxuryButton>
                </Box>
              </CardContent>
            </LuxuryCard>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
          >
            <LuxuryCard elevation={3}>
              <CardContent sx={{ p: 4 }}>
                <PremiumHeader
                  title="Payment"
                  subtitle="Secure and convenient payment options"
                  icon={<PaymentIcon sx={{ fontSize: 32 }} />}
                />

                <PaymentSection
                  totalAmount={calculateTotal()}
                  discountAmount={discountAmount}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  membershipId={membershipId}
                  setMembershipId={setMembershipId}
                  membershipVerified={membershipVerified}
                  onVerifyMembership={handleVerifyMembership}
                />

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <LuxuryButton variant="outlined" onClick={() => setActiveStep(2)}>
                    Back
                  </LuxuryButton>
                  <LuxuryButton 
                    variant="contained" 
                    color="success"
                    size="large"
                    onClick={handlePayment}
                    startIcon={<PaymentIcon />}
                  >
                    Pay ₹{calculateTotal() - discountAmount}
                  </LuxuryButton>
                </Box>
              </CardContent>
            </LuxuryCard>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
          >
            {registration && (
              <LuxuryCard elevation={3}>
                <CardContent sx={{ p: 4 }}>
                  <PremiumHeader
                    title="Registration Complete"
                    subtitle="Your registration has been successfully processed"
                    icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
                  />

                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 3,
                      backgroundColor: alpha(colors.emerald, 0.1),
                      color: colors.navy,
                      border: `1px solid ${colors.emerald}`,
                      '& .MuiAlert-icon': {
                        color: colors.emerald,
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: colors.emerald }}>
                      Registration Successful!
                    </Typography>
                    <Typography>Registration No: {registration.registrationNo}</Typography>
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid size={{xs:12,sm:6}}>
                      <Typography variant="subtitle1" gutterBottom sx={{ 
                        color: colors.navy,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <SecurityIcon sx={{ color: colors.gold }} />
                        Sample Collection QR Codes
                      </Typography>
                      <QRCodes tests={registration.tests} />
                    </Grid>

                    <Grid size={{xs:12,sm:6}}>
                      <div ref={receiptRef}>
                        <Receipt registration={registration} />
                      </div>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <LuxuryButton
                      variant="contained"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                    >
                      Print Receipt
                    </LuxuryButton>
                    <LuxuryButton
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      onClick={handleSendWhatsApp}
                      sx={{
                        background: `linear-gradient(135deg, #25D366 0%, #128C7E 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, #128C7E 0%, #25D366 100%)`,
                        },
                      }}
                    >
                      Send WhatsApp
                    </LuxuryButton>
                    <LuxuryButton
                      variant="contained"
                      startIcon={<EmailIcon />}
                      sx={{
                        background: `linear-gradient(135deg, #EA4335 0%, #FBBC05 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, #FBBC05 0%, #EA4335 100%)`,
                        },
                      }}
                    >
                      Send Email
                    </LuxuryButton>
                    <LuxuryButton
                      variant="outlined"
                      onClick={handleReset}
                    >
                      New Registration
                    </LuxuryButton>
                  </Box>
                </CardContent>
              </LuxuryCard>
            )}
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 2 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <LuxuryPaper elevation={2} sx={{ p: 2, mb: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={{xs:12,sm:6}}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ 
                    bgcolor: 'transparent',
                    background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                    width: 56, 
                    height: 56,
                    border: `3px solid ${colors.cream}`,
                    boxShadow: `0 5px 20px ${alpha(colors.gold, 0.3)}`,
                  }}>
                    <DiamondIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      Elite Diagnostics
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                      AI-Powered Prescription Registration • Corporate Health Solutions
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{xs:12,sm:6}}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Chip 
                    icon={<ConvenienceIcon />} 
                    label="Corporate Health Partner" 
                    sx={{
                      backgroundColor: alpha(colors.gold, 0.1),
                      border: `1px solid ${colors.gold}`,
                      color: colors.navy,
                      fontWeight: 600,
                    }}
                  />
                  <Chip 
                    icon={<SpeedIcon />} 
                    label="AI-Powered" 
                    sx={{
                      background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                      color: colors.cream,
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: colors.cream,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </LuxuryPaper>
        </motion.div>

        {/* Stepper */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Stepper activeStep={activeStep} sx={{ mb: 1 }}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: activeStep >= index ? colors.gold : alpha(colors.gold, 0.3),
                        color: activeStep >= index ? colors.cream : colors.navy,
                        transition: 'all 0.3s ease',
                      }}>
                        {step.icon}
                      </Avatar>
                    )}
                  >
                    <Typography sx={{ 
                      color: activeStep >= index ? colors.navy : alpha(colors.charcoal, 0.5),
                      fontWeight: activeStep >= index ? 600 : 400,
                    }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </motion.div>
        )}

        {/* Mobile Stepper Indicator */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
              Step {activeStep + 1} of {steps.length}: {steps[activeStep].label}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(activeStep + 1) * 25} 
              sx={{ 
                mt: 1, 
                height: 8, 
                borderRadius: 4,
                backgroundColor: alpha(colors.gold, 0.2),
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {getStepContent(activeStep)}
        </AnimatePresence>

        {/* Camera Dialog */}
        <CameraDialog
          open={showCamera}
          onClose={() => setShowCamera(false)}
          onCapture={handleCapture}
        />
      </Box>
    </Container>
  );
};
